/**
 * Social Meal Requests Module
 * Allows clients to ask another specific client to buy a meal for them
 * ("friend-to-friend meal request / food wishlist").
 * Uses the Firestore modular Web SDK via the global `window.db`.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  getDocs,
  limit,
} from "https://www.gstatic.com/firebasejs/12.18.0/firebase-firestore.js";

export const MEAL_REQUEST_STATUSES = {
  OPEN: "open",
  ACCEPTED: "accepted",
  DECLINED: "declined",
  FULFILLED: "fulfilled",
  EXPIRED: "expired",
  CANCELLED: "cancelled",
};

/**
 * Create a social meal request directed at a specific friend.
 *
 * @param {object} db - Firestore instance
 * @param {object} params
 * @param {string} params.requesterId
 * @param {string} params.requesterName
 * @param {string} params.targetFriendId   - UID of the friend being asked to pay
 * @param {string} params.targetFriendEmail
 * @param {string} params.providerId
 * @param {string} params.providerName
 * @param {Array}  params.items            - [{ menuItemId, title, quantity, price }]
 * @param {number} params.totalAmount
 * @param {string} params.currency
 * @param {string} [params.message]
 * @param {number} [params.expiresInHours] - default 24
 * @returns {Promise<string>} requestId
 */
export async function createMealRequest(db, {
  requesterId,
  requesterName,
  targetFriendId,
  targetFriendEmail,
  providerId,
  providerName,
  items,
  totalAmount,
  currency = "USD",
  message = "",
  expiresInHours = 24,
}) {
  if (!requesterId || !targetFriendId || !providerId) {
    throw new Error("requesterId, targetFriendId, and providerId are required.");
  }
  if (requesterId === targetFriendId) {
    throw new Error("You cannot request a meal from yourself.");
  }
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error("At least one item is required.");
  }
  if (typeof totalAmount !== "number" || totalAmount <= 0) {
    throw new Error("totalAmount must be a positive number.");
  }

  const expiresAt = new Date(Date.now() + expiresInHours * 60 * 60 * 1000);

  const docRef = await addDoc(collection(db, "socialMealRequests"), {
    requesterId,
    requesterName: requesterName || "",
    targetFriendId,
    targetFriendEmail: targetFriendEmail || "",
    providerId,
    providerName: providerName || "",
    items,
    message,
    totalAmount,
    currency,
    status: MEAL_REQUEST_STATUSES.OPEN,
    fulfilledByGiftOrderId: null,
    expiresAt,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Cancel a meal request (requester only — enforced by Firestore rules).
 */
export async function cancelMealRequest(db, requestId) {
  await updateDoc(doc(db, "socialMealRequests", requestId), {
    status: MEAL_REQUEST_STATUSES.CANCELLED,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Decline a meal request (target friend declines — enforced by Firestore rules).
 */
export async function declineMealRequest(db, requestId) {
  await updateDoc(doc(db, "socialMealRequests", requestId), {
    status: MEAL_REQUEST_STATUSES.DECLINED,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Mark a meal request as fulfilled (linked to a gift order).
 * Called after the target friend creates a giftOrder to fulfill it.
 */
export async function fulfillMealRequest(db, requestId, giftOrderId) {
  await updateDoc(doc(db, "socialMealRequests", requestId), {
    status: MEAL_REQUEST_STATUSES.FULFILLED,
    fulfilledByGiftOrderId: giftOrderId,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Real-time listener: requests SENT BY this client (their own outgoing requests).
 */
export function listenToSentRequests(db, requesterId, callback) {
  const q = query(
    collection(db, "socialMealRequests"),
    where("requesterId", "==", requesterId),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    const requests = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(requests);
  });
}

/**
 * Real-time listener: requests sent TO this client (incoming requests to pay for a friend).
 */
export function listenToIncomingRequests(db, targetFriendId, callback) {
  const q = query(
    collection(db, "socialMealRequests"),
    where("targetFriendId", "==", targetFriendId),
    where("status", "==", MEAL_REQUEST_STATUSES.OPEN),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    const requests = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(requests);
  });
}

/**
 * Friend picker search: query clients by displayName or email.
 * Only returns clients who have opted in (allowFriendRequests == true).
 * @param {object} db
 * @param {string} searchTerm  - partial name or email
 * @param {string} excludeUid  - exclude the current user from results
 * @returns {Promise<Array>}   - array of { uid, displayName, email }
 */
export async function searchFriends(db, searchTerm, excludeUid) {
  if (!searchTerm || searchTerm.trim().length < 2) return [];

  const term = searchTerm.trim().toLowerCase();
  const results = new Map();

  // Search by email (exact lowercase match)
  const emailSnap = await getDocs(
    query(
      collection(db, "clients"),
      where("allowFriendRequests", "==", true),
      where("email", "==", term),
      limit(10)
    )
  );
  emailSnap.forEach((d) => {
    if (d.id !== excludeUid) results.set(d.id, { uid: d.id, ...d.data() });
  });

  // Search by displayName prefix (Firestore range trick)
  const nameEnd = term.replace(/.$/, (c) => String.fromCharCode(c.charCodeAt(0) + 1));
  const nameSnap = await getDocs(
    query(
      collection(db, "clients"),
      where("allowFriendRequests", "==", true),
      where("displayNameLower", ">=", term),
      where("displayNameLower", "<", nameEnd),
      limit(10)
    )
  );
  nameSnap.forEach((d) => {
    if (d.id !== excludeUid) results.set(d.id, { uid: d.id, ...d.data() });
  });

  return Array.from(results.values()).map((c) => ({
    uid: c.uid,
    displayName: c.displayName || c.name || c.email || "Unknown",
    email: c.email || "",
  }));
}
